import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FolderArchive, FileText, Send, Download, CheckSquare, Square, Link2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient, authFetch } from "@/lib/apiClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Steuer-Ordner: Jahresüberblick aller Steuer-PDFs (MWST, Bezugsteuer, Abschluss, Archiv) + Versand an den Treuhänder. */
export default function TaxOverviewCard({ companyId }) {
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [shares, setShares] = useState([]);
  const loadShares = (cid, y) => apiClient.get(`/companies/${cid}/documents/tax-overview/shares?year=${y}`).then((r) => setShares(r.data || [])).catch(() => setShares([]));

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    apiClient.get(`/companies/${companyId}/documents/tax-overview?year=${year}`)
      .then((r) => {
        setData(r.data);
        setRecipient((prev) => prev || r.data.trustee_email || "");
        setSel(new Set(r.data.sections.flatMap((s) => s.items).filter((i) => !i.zip).map((i) => i.id)));
      })
      .catch(() => toast.error("Steuer-Übersicht konnte nicht geladen werden"))
      .finally(() => setLoading(false));
    loadShares(companyId, year);
  }, [companyId, year]);

  const copy = async (url) => { try { await navigator.clipboard.writeText(url); toast.success("Link kopiert"); } catch { window.prompt("Link kopieren:", url); } };
  const createShare = async () => {
    if (!sel.size) { toast.error("Bitte mindestens ein Dokument wählen"); return; }
    setSending(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/documents/tax-overview/share`, { year, item_ids: [...sel], recipient: recipient || undefined, message });
      await copy(r.data.url);
      toast.success(`Freigabe-Link erstellt (${r.data.files.length} Dokumente, 30 Tage gültig)`);
      loadShares(companyId, year);
    } catch (e) { toast.error(e.response?.data?.detail || "Freigabe konnte nicht erstellt werden"); }
    finally { setSending(false); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Freigabe-Link widerrufen? Der Treuhänder kann die Unterlagen danach nicht mehr öffnen.")) return;
    try { await apiClient.delete(`/companies/${companyId}/documents/tax-overview/shares/${id}`); toast.success("Freigabe widerrufen"); loadShares(companyId, year); }
    catch (e) { toast.error(e.response?.data?.detail || "Widerruf fehlgeschlagen"); }
  };
  const STATUS = { open: ["Offen", "bg-slate-100 text-slate-700"], approved: ["Geprüft ✓", "bg-emerald-100 text-emerald-800"], changes_requested: ["Rückfrage", "bg-amber-100 text-amber-900"] };

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const download = async (item) => {
    try {
      const r = await authFetch(`${BACKEND_URL}${item.url}`);
      if (!r.ok) throw new Error();
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a"); a.href = url; a.download = item.filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error(`${item.title}: Download fehlgeschlagen`); }
  };

  const send = async () => {
    if (!recipient) { toast.error("Bitte E-Mail des Treuhänders angeben"); return; }
    if (!sel.size) { toast.error("Bitte mindestens ein Dokument wählen"); return; }
    setSending(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/documents/tax-overview/send`, { year, recipient, item_ids: [...sel], message });
      toast.success(`${r.data.sent} Dokument(e) an ${r.data.recipient} gesendet (${r.data.size_mb} MB) – inkl. Freigabe-Link` + (r.data.failed?.length ? ` – nicht erzeugt: ${r.data.failed.join(", ")}` : ""));
      setData((d) => ({ ...d, sent: [{ at: new Date().toISOString(), recipient, count: r.data.sent }, ...(d?.sent || [])] }));
      loadShares(companyId, year);
    } catch (e) { toast.error(e.response?.data?.detail || "Versand fehlgeschlagen"); loadShares(companyId, year); }
    finally { setSending(false); }
  };

  const bundle = async () => {
    if (!sel.size) { toast.error("Bitte mindestens ein Dokument wählen"); return; }
    setSending(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/documents/tax-overview/bundle`, { year, item_ids: [...sel] }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a"); a.href = url; a.download = `Steuerunterlagen_${year}.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`ZIP mit ${sel.size} Dokument(en) erstellt`);
    } catch (e) { toast.error("ZIP konnte nicht erstellt werden"); }
    finally { setSending(false); }
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

  return (
    <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="tax-overview-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0"><FolderArchive className="h-5 w-5" /></div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Steuer-Ordner {year}</h2>
            <p className="text-sm text-slate-500 mt-0.5">Alle Steuer-PDFs eines Jahres auf einen Blick – herunterladen oder gesammelt an den Treuhänder senden.</p>
          </div>
        </div>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm" data-testid="tax-overview-year">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {loading || !data ? <p className="text-sm text-slate-500">Lädt …</p> : (
        <div className="space-y-4">
          {data.sections.filter((s) => s.items.length || s.key !== "archived").map((s) => (
            <div key={s.key} data-testid={`tax-overview-section-${s.key}`}>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{s.title}</div>
              {s.items.length === 0 ? (
                <p className="text-xs text-slate-400">{s.key === "reverse_charge" ? (data.vat_registered ? "Nicht relevant – Bezugsteuer wird in der MWST-Abrechnung deklariert." : "Keine Auslandsleistungen gezählt.") : s.key === "vat" ? "Firma ist nicht MWST-registriert." : "–"}</p>
              ) : (
                <ul className="divide-y divide-slate-100 rounded-md border border-slate-100">
                  {s.items.map((it) => (
                    <li key={it.id} className="flex items-center gap-3 px-3 py-2" data-testid={`tax-overview-item-${it.id.replace(/[^a-zA-Z0-9_-]/g, "_")}`}>
                      <button type="button" onClick={() => toggle(it.id)} className="text-slate-600 hover:text-slate-900" aria-label="auswählen" data-testid={`tax-overview-select-${it.id.replace(/[^a-zA-Z0-9_-]/g, "_")}`}>
                        {sel.has(it.id) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                      </button>
                      <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{it.title}</div>
                        {it.subtitle && <div className="text-xs text-slate-500 truncate">{it.subtitle}</div>}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => download(it)} data-testid={`tax-overview-download-${it.id.replace(/[^a-zA-Z0-9_-]/g, "_")}`}><Download className="h-4 w-4 mr-1" />PDF</Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="tax-overview-send-box">
            <div className="text-sm font-medium text-slate-900">An Treuhänder weiterleiten ({sel.size} ausgewählt)</div>
            <div className="grid gap-2 md:grid-cols-[1fr_2fr_auto]">
              <Input type="email" placeholder="treuhaender@kanzlei.ch" value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="tax-overview-recipient" />
              <Input placeholder="Nachricht (optional)" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="tax-overview-message" />
              <div className="flex gap-2">
                <Button onClick={send} disabled={sending || !sel.size} data-testid="tax-overview-send-button"><Send className="h-4 w-4 mr-1.5" />{sending ? "Sende …" : "Senden"}</Button>
                <Button variant="outline" onClick={bundle} disabled={sending || !sel.size} data-testid="tax-overview-zip-button"><FolderArchive className="h-4 w-4 mr-1.5" />Als ZIP</Button>
                <Button variant="outline" onClick={createShare} disabled={sending || !sel.size} data-testid="tax-overview-share-button" title="Sicheren Link erstellen und kopieren"><Link2 className="h-4 w-4 mr-1.5" />Freigabe-Link</Button>
              </div>
            </div>
            {shares.length > 0 && (
              <div className="pt-2 border-t border-slate-200" data-testid="tax-overview-shares">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Freigaben an den Treuhänder</div>
                <ul className="space-y-1">
                  {shares.map((sh) => (
                    <li key={sh.id} className="flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid={`tax-overview-share-${sh.id}`}>
                      <span className={`rounded-full px-2 py-0.5 font-semibold ${sh.revoked ? "bg-slate-200 text-slate-500 line-through" : STATUS[sh.status]?.[1]}`} data-testid={`tax-overview-share-status-${sh.id}`}>{sh.revoked ? "Widerrufen" : STATUS[sh.status]?.[0]}</span>
                      <span>{fmtTime(sh.created_at)}{sh.recipient ? ` · ${sh.recipient}` : ""} · {sh.files.length} Dok. · {sh.views}× geöffnet</span>
                      {sh.reviewer_name && <span className="text-slate-800">– {sh.reviewer_name}{sh.comment ? `: «${sh.comment}»` : ""}</span>}
                      {!sh.revoked && sh.status !== "approved" && <button type="button" onClick={() => revoke(sh.id)} className="text-rose-600 hover:underline" data-testid={`tax-overview-share-revoke-${sh.id}`}>widerrufen</button>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data.sent?.length > 0 && (
              <div className="text-xs text-slate-500" data-testid="tax-overview-sent-log">Zuletzt gesendet: {data.sent.map((x) => `${fmtTime(x.at)} an ${x.recipient} (${x.count})`).join(" · ")}</div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
